import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';

export default function CountrySearchSelect({
  countries,
  loading,
  value,
  onChange,
  disabled = false,
  placeholder = 'Type to search countries...',
  invalid = false,
}) {
  const listboxId = useId();
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return countries;
    }
    return countries.filter((c) => c.name.toLowerCase().includes(q));
  }, [countries, query]);

  const closeDropdown = useCallback(() => {
    setOpen(false);
    setHighlightIndex(-1);
  }, []);

  const selectCountry = useCallback(
    (country) => {
      onChange(country.name);
      setQuery(country.name);
      closeDropdown();
    },
    [onChange, closeDropdown]
  );

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        closeDropdown();
        if (value) {
          setQuery(value);
        }
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [closeDropdown, value]);

  useEffect(() => {
    if (highlightIndex >= filtered.length) {
      setHighlightIndex(filtered.length > 0 ? filtered.length - 1 : -1);
    }
  }, [filtered.length, highlightIndex]);

  const handleInputChange = (event) => {
    const next = event.target.value;
    setQuery(next);
    setOpen(true);
    setHighlightIndex(0);
    if (!next.trim()) {
      onChange('');
    }
  };

  const handleInputFocus = () => {
    if (!loading && !disabled) {
      setOpen(true);
    }
  };

  const handleKeyDown = (event) => {
    if (loading || disabled) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeDropdown();
      if (value) {
        setQuery(value);
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        setHighlightIndex(filtered.length > 0 ? 0 : -1);
        return;
      }
      if (filtered.length === 0) {
        return;
      }
      setHighlightIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        setHighlightIndex(filtered.length > 0 ? filtered.length - 1 : -1);
        return;
      }
      if (filtered.length === 0) {
        return;
      }
      setHighlightIndex((prev) => (prev > 0 ? prev - 1 : filtered.length - 1));
      return;
    }

    if (event.key === 'Enter') {
      if (!open || highlightIndex < 0 || !filtered[highlightIndex]) {
        return;
      }
      event.preventDefault();
      selectCountry(filtered[highlightIndex]);
    }
  };

  const activeDescendant =
    open && highlightIndex >= 0 && filtered[highlightIndex]
      ? `${listboxId}-option-${filtered[highlightIndex].code}`
      : undefined;

  return (
    <div className="rwm-country-select" ref={rootRef}>
      <Form.Control
        ref={inputRef}
        type="text"
        className={`rwm-country-select__input${invalid ? ' is-invalid' : ''}`}
        placeholder={loading ? 'Loading countries…' : placeholder}
        value={query}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        disabled={disabled || loading}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={activeDescendant}
        aria-autocomplete="list"
        data-testid="rwm-country-select-input"
      />
      {open && !loading && !disabled ? (
        <div
          id={listboxId}
          className="rwm-country-select__dropdown"
          role="listbox"
          data-testid="rwm-country-select-dropdown"
        >
          {filtered.length === 0 ? (
            <div className="rwm-country-select__empty">No countries found</div>
          ) : (
            filtered.map((country, index) => (
              <button
                key={country.code}
                type="button"
                id={`${listboxId}-option-${country.code}`}
                role="option"
                aria-selected={value === country.name}
                className={`rwm-country-select__option${
                  index === highlightIndex ? ' rwm-country-select__option--highlighted' : ''
                }`}
                data-testid={`rwm-country-select-option-${country.code}`}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setHighlightIndex(index)}
                onClick={() => selectCountry(country)}
              >
                {country.name}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
